import { RaRecord } from 'react-admin';

// ============================================
// ENUMS
// ============================================

/**
 * Channel types supported by the platform.
 */
export enum ChannelType {
  ZAPY = 'zapy',
  TELEGRAM = 'telegram',
  EMAIL = 'email',
  // Reserved for future use
  // WHATSAPP_OFFICIAL = 'whatsapp_official',
}

/** Human-readable display names for each channel type */
export const CHANNEL_TYPE_NAMES: Record<ChannelType, string> = {
  [ChannelType.ZAPY]: 'Zapy',
  [ChannelType.TELEGRAM]: 'Telegram',
  [ChannelType.EMAIL]: 'Email',
};

/** Get human-readable name for a channel type, with fallback */
export function getChannelTypeName(type?: ChannelType | null): string | null {
  if (!type) return null;
  return CHANNEL_TYPE_NAMES[type] ?? type;
}

/**
 * Ticket lifecycle statuses.
 * Matches backend TicketStatus enum.
 */
export enum TicketStatus {
  PENDING = 'pending',
  OPEN = 'open',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

export enum TicketPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  STICKER = 'sticker',
  LOCATION = 'location',
  LIVE_LOCATION = 'live_location',
  CONTACT = 'contact',
  POLL = 'poll',
  CALL = 'call',
  SYSTEM = 'system',
}

export enum MessageStatus {
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  SENT = 'SENT',
  RECEIVED = 'RECEIVED',
  READ = 'READ',
  PLAYED = 'PLAYED',
  FAILED = 'FAILED',
}

/**
 * Message status event from WebSocket
 * Received when a message status changes (e.g., PENDING → SENT → RECEIVED → READ)
 */
export interface MessageStatusEvent {
  conversationId: number;
  messageId: number;
  status: MessageStatus;
  timestamp: string;
  errorDetails?: Record<string, unknown> | null;
}

export enum ParticipantRole {
  OWNER = 'owner',
  AGENT = 'agent',
  OBSERVER = 'observer',
}

export enum ConversationSource {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

/**
 * Types of conversations.
 * Currently only EXTERNAL is used for customer conversations via Zapy.
 */
export enum ConversationType {
  /** External conversations with customers (WhatsApp via Zapy) */
  EXTERNAL = 'external',
}

/**
 * Visibility options (reserved for future internal channels feature)
 */
export enum ConversationVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

// ============================================
// ENTITY INTERFACES
// ============================================

/**
 * Channel entity - represents a channel type (e.g., Zapy/WhatsApp).
 * Configuration has moved to ChannelConnection for per-connection settings.
 */
export interface Channel extends RaRecord {
  uuid: string;
  type: ChannelType;
  name: string;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * ChannelConnection entity - represents a physical connection instance (e.g., a Zapy instance).
 * Each connection can have its own config, ticket settings, and default queue.
 */
export interface ChannelConnection extends RaRecord {
  uuid: string;
  channelId: number;
  channel?: Channel;
  name: string;
  identifier?: string;
  displayName?: string | null;
  status?: ChannelConnectionStatus;
  lastSyncAt?: string | null;
  credentials?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  /** Per-connection ticket configuration */
  ticketConfig?: TicketConfig | null;
  /** Per-connection settings (ignoreGroupMessages, notifyGroupMessages) */
  config?: ChannelConnectionConfig | null;
  /** Default queue for tickets created from this connection */
  defaultQueueId?: number | null;
  defaultQueue?: TicketQueue | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Configuration for a ChannelConnection.
 * Settings that control how messages/tickets are handled for this specific connection.
 */
export interface ChannelConnectionConfig {
  /** If true, ignore all group messages (don't create conversations/tickets) */
  ignoreGroupMessages?: boolean;
  /** If true, create conversations for group messages but don't create tickets */
  notifyGroupMessages?: boolean;
}

/**
 * Status of a channel connection
 */
export enum ChannelConnectionStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  QR_PENDING = 'qr_pending',
  QR_TIMEOUT = 'qr_timeout',
  STOPPED = 'stopped',
  ERROR = 'error',
}

export interface Contact extends RaRecord {
  uuid: string;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  avatarFileId?: number | null;
  profilePicUrl?: string | null;
  customFields?: Record<string, unknown> | null;
  lastActivityAt?: string | null;
  mergedIntoContactId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContactIdentity extends RaRecord {
  uuid: string;
  contactId: number;
  contact?: Contact;
  channelConnectionId: number;
  channelType?: ChannelType | null;
  externalId: string;
  name?: string | null;
  avatarUrl?: string | null;
  presence?: string;
  isGroup: boolean;
  profileData?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Conversation entity - represents a chat conversation with a customer.
 */
export interface Conversation extends RaRecord {
  uuid: string;
  channelConnectionId?: number | null;
  channelConnection?: ChannelConnection | null;
  contactId?: number | null;
  contact?: Contact | null;
  lastMessage?: string | null;
  lastMessageType?: MessageType | null;
  lastMessageId?: number | null;
  source?: ConversationSource | null;
  firstResponseTime?: string | null;
  isGroup: boolean;
  chatbot: boolean;
  ticketConfig?: TicketConfig | null;
  contactLastSeenAt?: string | null;
  lastActivityAt?: string | null;
  snoozedUntil?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;

  /** Type of conversation (currently only 'external' for customer chats) */
  type?: ConversationType | null;
  /** Visibility (reserved for future use) */
  visibility?: ConversationVisibility | null;
  /** Name (reserved for future use) */
  name?: string | null;
  /** Description (reserved for future use) */
  description?: string | null;
  /** Avatar file ID (reserved for future use) */
  avatarFileId?: number | null;
  /** User who created the conversation */
  createdByUserId?: number | null;
  createdByUser?: User | null;
  // Reserved for future cross-tenant feature
  isCrossTenant?: boolean;
  mirrorConversationUuid?: string | null;

  // Denormalized open ticket (from list endpoint)
  openTicket?: {
    id: number;
    uuid: string;
    status: string;
  } | null;

  // Most recent ticket regardless of status (from list endpoint)
  latestTicket?: {
    id: number;
    status: string;
  } | null;

  // Relations (optional, populated when fetched with relations)
  messages?: Message[];
  tickets?: Ticket[];
  tags?: TagAssignment[];
  participants?: ConversationParticipant[];
}

export interface ConversationParticipant extends RaRecord {
  uuid: string;
  conversationId: number;
  conversation?: Conversation;
  userId?: number | null;
  user?: User | null;
  contactIdentityId?: number | null;
  contactIdentity?: ContactIdentity | null;
  role: ParticipantRole;
  joinedAt: string;
  leftAt?: string | null;
  removedAt?: string | null;
  lastSeenAt?: string | null;
  lastReadMessageId?: number | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// MESSAGE METADATA TYPES
// =============================================================================

/** Info about the original message when this is a forwarded message */
export interface ForwardedFromMeta {
  messageId?: number;
  messageUuid?: string;
  conversationId?: number;
  conversationUuid?: string;
  senderName?: string;
}

/** Zapy/WhatsApp-specific message metadata */
export interface ZapyMessageMeta {
  mentionedJids?: string[];
  ephemeral?: boolean;
  ephemeralDuration?: number;
}

/** Telegram-specific message metadata */
export interface TelegramMessageMeta {
  chatType?: 'private' | 'group' | 'supergroup' | 'channel';
  topicName?: string;
  isForum?: boolean;
  callbackQueryId?: string;
}

/** Email-specific message metadata */
export interface EmailMessageMeta {
  messageId?: string;
  inReplyTo?: string;
  references?: string[];
  from?: string;
  to?: string[];
  cc?: string[];
  subject?: string;
}

/** Typed metadata for messages, mirroring the backend MessageMetadata interface */
export interface MessageMetadata {
  quotedMessageId?: string;
  systemEventKey?: string;
  forwardedFrom?: ForwardedFromMeta;
  zapy?: ZapyMessageMeta;
  telegram?: TelegramMessageMeta;
  email?: EmailMessageMeta;
  [key: string]: unknown;
}

export interface Message extends RaRecord {
  uuid: string;
  externalId?: string | null;
  conversationId: number;
  conversation?: Conversation;
  ticketId?: number | null;
  ticket?: Ticket | null;
  senderUserId?: number | null;
  senderUser?: User | null;
  senderContactIdentityId?: number | null;
  senderContactIdentity?: ContactIdentity | null;
  type: MessageType;
  body?: string | null;
  isPrivate: boolean;
  /**
   * For external channels (WhatsApp, etc): true if message was sent by an agent,
   * false if received from the customer.
   */
  fromMe?: boolean | null;
  isDeleted: boolean;
  deletedByUserId?: number | null;
  deletedByContactIdentityId?: number | null;
  isEdited: boolean;
  viewOnce: boolean;
  quotedMessageId?: number | null;
  quotedMessage?: Message | null;
  topicId?: string | null;
  status: MessageStatus;
  sentAt?: string | null;
  deliveredAt?: string | null;
  readAt?: string | null;
  metadata?: MessageMetadata | null;
  errorDetails?: Record<string, unknown> | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  // Relations
  attachments?: MessageAttachment[];
  reactions?: MessageReaction[];
}

export enum AttachmentFileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  GIF = 'gif',
  DOCUMENT = 'document',
  STICKER = 'sticker',
  LOCATION = 'location',
  CONTACT = 'contact',
}

export interface MessageAttachment extends RaRecord {
  uuid: string;
  messageId: number;
  fileId?: number | null;
  thumbnailFileId?: number | null;
  fileType: AttachmentFileType;
  fileName?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  caption?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface MessageReaction extends RaRecord {
  messageId: number;
  userId?: number | null;
  contactIdentityId?: number | null;
  reaction: string;
  createdAt: string;
}

export interface MessageEditHistory extends RaRecord {
  messageId: number;
  previousBody?: string | null;
  editedAt: string;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
}

export interface Ticket extends RaRecord {
  uuid: string;
  conversationId: number;
  conversation?: Conversation;
  status: TicketStatus;
  priority?: TicketPriority | null;
  ticketQueueId?: number | null;
  ticketQueue?: TicketQueue | null;
  queue?: TicketQueue | null;
  assignedUserId?: number | null;
  assignedUser?: User | null;
  startMessageId?: number | null;
  endMessageId?: number | null;
  contactId?: number | null;
  contact?: Contact | null;
  channelType?: ChannelType | null;
  channelTypeName?: string | null;
  channelConnectionName?: string | null;
  lastMessage?: string | null;
  lastMessageType?: string | null;
  createdAt: string;
  updatedAt: string;
  // Relations
  messages?: Message[];
  tags?: TagAssignment[];
}

export interface TicketQueue extends RaRecord {
  uuid: string;
  name: string;
  description?: string | null;
  color?: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum TagDomain {
  TICKET = 'ticket',
  CONVERSATION = 'conversation',
  JOB_APPLICATION = 'job_application',
}

export interface Tag extends RaRecord {
  uuid: string;
  domain: TagDomain;
  name: string;
  color?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TagAssignment extends RaRecord {
  domain: TagDomain;
  domainId: number;
  tagId: number;
  tag?: Tag;
  createdAt: string;
}

/**
 * Ticket creation mode for a conversation or connection.
 */
export enum TicketConfigMode {
  /** Automatically create tickets for new messages */
  AUTO = 'auto',
  /** Agents manually create tickets as needed */
  MANUAL = 'manual',
  /** Never create tickets (chat-only mode) */
  OFF = 'off',
}

/**
 * Configuration for ticket creation behavior.
 */
export interface TicketConfig {
  mode: TicketConfigMode;
}

// User type (simplified, assuming it exists elsewhere)
export interface User extends RaRecord {
  uuid: string;
  name?: string | null;
  email: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// WEBSOCKET EVENT TYPES
// ============================================

export interface WsConnectionAuth {
  tenantUuid: string;
  token: string;
}

export interface WsSubscribePayload {
  conversationUuid?: string;
  ticketUuid?: string;
  queueUuid?: string;
}

export interface WsMessagePayload {
  conversationId: number;
  message: Message;
}

/**
 * Payload for chat:message-updated events
 * Contains partial updates to apply to an existing message
 */
export interface WsMessageUpdatedPayload {
  conversationId: number;
  messageId: number;
  updates: Partial<Pick<Message, 'body' | 'isEdited' | 'isDeleted' | 'status' | 'metadata'>>;
}

export interface WsTypingPayload {
  conversationId: number;
  userId?: number;
  userEmail?: string;
}

export interface WsTicketPayload {
  ticketUuid: string;
  status?: TicketStatus;
  previousStatus?: TicketStatus;
  priority?: TicketPriority;
  previousPriority?: TicketPriority;
  assignedUserId?: number | null;
  assignedUserEmail?: string | null;
  changedBy?: { id: number; email: string };
  timestamp: string;
}

export interface WsReactionPayload {
  conversationId: number;
  messageId: number;
  reactionId: number;
  emoji: string;
  userId?: number;
  contactIdentityId?: number;
}

export interface WsParticipantPayload {
  conversationId: number;
  participant?: ConversationParticipant;
  userId?: number;
  removedBy?: { id: number; email: string };
}

export interface WsReadReceiptPayload {
  conversationId: number;
  userId: number;
  lastReadMessageId: number;
  readAt: string;
}

// React Admin ra-realtime event format
export interface RaRealtimeEvent {
  type: 'created' | 'updated' | 'deleted';
  resource: string;
  payload: {
    ids: (string | number)[];
    data?: Record<string, unknown>;
  };
}

// Access level returned on subscription
export interface AccessLevel {
  isParticipant?: boolean;
  hasGlobalPermission?: boolean;
  isAssigned?: boolean;
  isInQueue?: boolean;
  role?: ParticipantRole;
}

export interface WsSubscribedPayload {
  conversationId?: number;
  ticketUuid?: string;
  accessLevel: AccessLevel;
}

// ============================================
// CHANNEL SOCKET TYPES
// ============================================

/**
 * Status of a Zapy (WhatsApp) instance
 */
export type ZapyInstanceStatusType =
  | 'connected'
  | 'disconnected'
  | 'connecting'
  | 'pending_qr_code_scan'
  | 'not_found'
  | 'error';

/**
 * Payload for channel subscription confirmation
 */
export interface WsChannelSubscribedPayload {
  channelConnectionId: number;
}

/**
 * Payload for channel status change events
 */
export interface WsChannelStatusChangedPayload {
  channelConnectionId: number;
  status: ZapyInstanceStatusType;
  profileName?: string;
  ownerJid?: string;
  timestamp: string;
}

/**
 * Payload for QR code update events
 */
export interface WsChannelQRUpdatedPayload {
  channelConnectionId: number;
  qrCode: string;
  expiresAt?: number;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT WEBSOCKET PAYLOADS
// ─────────────────────────────────────────────────────────────────────────────

export interface WsContactUpdatedPayload {
  contactId: number;
  name?: string;
  avatarFileId?: number | null;
  timestamp: string;
}
